import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, http } from '../client.ts';
import type { Visitor } from '../schemas/visitor.ts';

/**
 * Visitor-specific hooks. Distinct from the generic CRUD because:
 *   - `useList` takes a role filter and reads X-Total-Count from headers
 *   - `useOne` doesn't need a role filter (single-fetch is not role-gated)
 *   - PUT/POST use the same `/api/visitors` path with id-or-null body
 */

export interface VisitorListParams {
  role: number;
  page?: number;
  size?: number;
}

export interface VisitorList {
  rows: Visitor[];
  totalCount: number;
}

export function useVisitors(params: VisitorListParams) {
  return useQuery({
    queryKey: ['visitors', 'list', params],
    queryFn: async (): Promise<VisitorList> => {
      const res = await api.get<Visitor[]>('/api/visitors', {
        params: { role: params.role, page: params.page ?? 0, size: params.size ?? 20 },
      });
      if (res.status !== 200) {
        throw new Error(`/api/visitors → ${res.status}`);
      }
      const totalRaw = res.headers['x-total-count'] ?? res.headers['X-Total-Count'];
      const totalCount = Number(totalRaw ?? res.data.length);
      return { rows: res.data, totalCount };
    },
    enabled: Number.isFinite(params.role) && params.role !== -1,
  });
}

export function useVisitor(id: number | null | undefined) {
  return useQuery({
    queryKey: ['visitor', id],
    queryFn: () => http.get<Visitor>(`/api/visitors/${id}`),
    enabled: id != null,
  });
}

export function useSaveVisitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Visitor) => {
      // POST when no id, PUT when id is present (matches JHipster convention).
      if (v.id == null) return http.post<Visitor>('/api/visitors', v);
      return http.put<Visitor>('/api/visitors', v);
    },
    onSuccess: saved => {
      qc.invalidateQueries({ queryKey: ['visitors'] });
      if (saved.id != null) qc.invalidateQueries({ queryKey: ['visitor', saved.id] });
    },
  });
}

export function useDeleteVisitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => http.delete(`/api/visitors/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visitors'] }),
  });
}

/**
 * Caller's Role-entity memberships, used to drive the role selector. Cached
 * for the session — these change rarely.
 */
export interface UserRole {
  id: number;
  name: string;
}
export function useMyRoles() {
  return useQuery({
    queryKey: ['my-roles'],
    queryFn: () => http.get<UserRole[]>('/api/roles/currentUser'),
    staleTime: 5 * 60_000,
  });
}
